import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAppStore } from './store';
import { useChatStore } from './store/chatStore';
import AuthProvider from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import Dashboard from './pages/Dashboard';
import EntityExplorer from './pages/EntityExplorer';
import GraphExplorer from './pages/GraphExplorer';
import AlertCenter from './pages/AlertCenter';
import AnalysisWorkbench from './pages/AnalysisWorkbench';
import CyberIntel from './pages/CyberIntel';
import SimulationPage from './pages/SimulationPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import InvestigationWizard from './pages/InvestigationWizard';
import JobsPage from './pages/JobsPage';
import BiometricsPage from './pages/BiometricsPage';
import EntityProfilePage from './pages/EntityProfilePage';
import SentinelPage from './pages/SentinelPage';
import AutomationPage from './pages/AutomationPage';
import MonitoringPage from './pages/MonitoringPage';
import GeoMapPage from './pages/GeoMapPage';
import CommunitiesPage from './pages/CommunitiesPage';
import ComparePage from './pages/ComparePage';
import AlertRulesPage from './pages/AlertRulesPage';
import AuditPage from './pages/AuditPage';
import WorkflowsPage from './pages/WorkflowsPage';
import DeduplicationPage from './pages/DeduplicationPage';
import AnomalyPage from './pages/AnomalyPage';
import ImportPage from './pages/ImportPage';
import WebhooksPage from './pages/WebhooksPage';
import TarifsPage from './pages/TarifsPage';
import BackupPage from './pages/BackupPage';
import AIChatPanel from './components/chat/AIChatPanel';
import CommandPalette from './components/search/CommandPalette';
import ToastContainer from './components/notifications/ToastContainer';
import AlertBanner from './components/notifications/AlertBanner';
import NotificationPanel from './components/notifications/NotificationPanel';
import { useNotifications } from './hooks/useNotifications';

function AppLayout() {
  const sidebarCollapsed = useAppStore((s) => s.sidebar.collapsed);
  const toggleChat = useChatStore((s) => s.toggleChat);

  // Connect WebSocket to notification system
  useNotifications();

  return (
    <div className="flex h-screen overflow-hidden bg-surface-primary">
      <Sidebar />
      <div
        className={`flex flex-col flex-1 min-w-0 transition-all duration-300 ${
          sidebarCollapsed ? 'ml-16' : 'ml-64'
        }`}
      >
        <AlertBanner />
        <TopBar />
        <main className="flex-1 overflow-auto p-6">
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/entities" element={<EntityExplorer />} />
            <Route path="/entities/:id" element={<EntityProfilePage />} />
            <Route path="/graph" element={<GraphExplorer />} />
            <Route path="/alerts" element={<AlertCenter />} />
            <Route path="/analysis" element={<AnalysisWorkbench />} />
            <Route path="/cyber" element={<CyberIntel />} />
            <Route path="/simulation" element={<SimulationPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/osint" element={<Navigate to="/investigation" replace />} />
            {/* ⚠️⚠️ FACEGATE RETIRE LE 06/10/2026 — DECISION DU FONDATEUR.
                Ces routes (et /settings plus bas) etaient enveloppees dans
                <FaceGate> : « Zone sensible — verification biometrique requise ».

                CE QU'IL PROTEGEAIT : rien. Mesure du 06/10/2026, avec un simple
                jeton porteur et AUCUN visage :
                    /api/v1/sentinel/dashboard   -> 200
                    /api/v1/faceai/users         -> 200
                    /api/v1/osint/investigations -> 200
                L'API ne verifie aucune biometrie. Le garde etait un rideau cote
                navigateur : il n'arretait que les porteurs de bonne foi, et se
                contournait avec un curl.

                CE QU'IL CASSAIT : la galerie visage est indexee par des UUID
                `mowibo_app.utilisateurs`, la session par un UUID `helios.users`
                — aucun compte natif Helios ne pouvait etre reconnu, par
                construction. Et son repli par mot de passe lisait
                `payload.email`, un claim que le jeton n'emettait pas : il
                retombait sur `sub` (un UUID), postait /auth/login avec, prenait
                401, et l'intercepteur global DETRUIT la session sur tout 401.
                L'utilisateur etait donc EJECTE vers /login meme avec le bon mot
                de passe — y compris sur /settings, la page ou il aurait change
                ce mot de passe.

                ⚠️ UNE PROTECTION DE CES ZONES DOIT ETRE SERVEUR : les points
                d'entree existent deja (`/sentinel/access-check`,
                `/sentinel/verify`, `/sentinel/liveness-check`, `/faceai/enroll`)
                et ne sont appeles par aucun garde. Les rebrancher cote client
                recreerait le meme rideau. */}
            <Route path="/investigation" element={<InvestigationWizard />} />
            <Route path="/biometrics" element={<BiometricsPage />} />
            <Route path="/sentinel" element={<SentinelPage />} />
            <Route path="/automation" element={<AutomationPage />} />
            <Route path="/monitoring" element={<MonitoringPage />} />
            <Route path="/geomap" element={<GeoMapPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/communities" element={<CommunitiesPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/alert-rules" element={<AlertRulesPage />} />
            <Route path="/workflows" element={<WorkflowsPage />} />
            <Route path="/dedup" element={<DeduplicationPage />} />
            <Route path="/anomaly" element={<AnomalyPage />} />
            <Route path="/import" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <ImportPage />
              </ProtectedRoute>
            } />
            <Route path="/webhooks" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <WebhooksPage />
              </ProtectedRoute>
            } />
            <Route path="/backup" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <BackupPage />
              </ProtectedRoute>
            } />
            {/* Tarifs versionnés et relevé du mois — administrateurs seulement,
                comme les routes serveur (`require_admin`). */}
            <Route path="/tarifs" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <TarifsPage />
              </ProtectedRoute>
            } />
            <Route path="/audit" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AuditPage />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute requiredRoles={['admin']}>
                {/* FaceGate retire ici aussi — voir le commentaire plus haut.
                    ⚠️ C'etait la QUATRIEME occurrence, celle qu'une relecture
                    manque : les trois autres sont sur une seule ligne chacune,
                    celle-ci est imbriquee dans un ProtectedRoute. Trouvee par
                    `grep FaceGate`, pas en relisant. */}
                <SettingsPage />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
      </div>

      {/* Command Palette (Cmd+K) */}
      <CommandPalette />

      {/* Chat IA Panel (slide-over) */}
      <AIChatPanel />

      {/* Bouton flottant Chat IA */}
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-cyber-cyan hover:bg-cyan-400 text-surface-primary shadow-lg shadow-cyber-cyan/25 flex items-center justify-center transition-all hover:scale-105"
        title="Chat IA Xawio"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
        </svg>
      </button>
    </div>
  );
}

export default function App() {
  const theme = useAppStore((s) => s.theme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.classList.toggle('light', theme === 'light');
  }, [theme]);

  return (
    <AuthProvider>
      <ToastContainer />
      <NotificationPanel />
      <Routes>
        {/* Public routes — no sidebar */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Protected routes — with sidebar/topbar layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />

        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AuthProvider>
  );
}
